import usePageTitle from "../utils/usePageTitle";
import React, { memo, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { Container, ErrorBlockGeneric, TextInput } from "common-tailwind-components";
import * as Dialog from "@radix-ui/react-dialog";
import * as Switch from "@radix-ui/react-switch";
import * as Tooltip from "@radix-ui/react-tooltip";
import { QRCodeSVG } from "qrcode.react";
import {
  AlertTriangle,
  ChevronRight,
  CircleHelp,
  Info,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  Shield,
  ShieldCheck,
  ShieldOff,
  X,
} from "lucide-react";

import { CapturePrivacyModal, Modal, UpdateInstallModal } from "../components";
import {
  SettingsAutoSaveFeedback,
  SettingsAutoSaveForm,
  SettingsControlSlot,
  SettingsFieldError,
  SettingsLoadState,
  SettingsRow,
  SettingsSection,
  SettingsSegmentedControl,
  SettingsSelect,
} from "../components/settings";
import type { SettingsChoice } from "../components/settings";
import {
  add_recording_screenshot_blacklist_apps,
  apply_update,
  approve_review_recording,
  change_own_password,
  check_for_updates,
  confirm_mfa_enrollment,
  disable_mfa,
  get_notification_settings,
  get_mfa_status,
  get_recording_screenshot_blacklist,
  get_recording_screenshot_format,
  get_recording_screenshot_redaction,
  get_recording_settings,
  get_review_recordings,
  put_notification_settings,
  put_recording_screenshot_format,
  put_recording_screenshot_redaction,
  put_recording_settings,
  remove_recording_screenshot_blacklist_apps,
  splitApiRequestFormErrors,
  start_mfa_enrollment,
} from "../api";
import type { MfaEnrollStartResponse } from "../api";
import type { NotificationChannel, NotificationFrequency, NotificationSettings } from "../api";
import type {
  RecordingScreenshotFormat,
  RedactionCategory,
  RedactionCategorySettings,
  UpdateStatus,
} from "../api/types";
import { useSettings } from "../utils/SettingsContext";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { supportedLanguages } from "../globals/i18n";
import useRecordingControls from "../utils/useRecordingControls";
import useAsyncInitialValues from "../utils/useAsyncInitialValues";
import { frontendCapabilities } from "../globals";

const PILL_BUTTON_CLASS =
  "inline-flex min-h-10 items-center gap-2 rounded-full border border-outline bg-surface px-3 text-sm font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

/** An on/off setting's two choices, Off first like a switch. */
const onOffChoices = <T,>(t: TFunction, on: T, off: T): SettingsChoice<T>[] => [
  { id: off, display: t("settings.toggle_off") },
  { id: on, display: t("settings.toggle_on") },
];

const isUpdateStatus = (value: UpdateStatus | { status: string }): value is UpdateStatus =>
  "currentVersion" in value;

type ScreenshotFormatFormValues = {
  screenshotFormat: RecordingScreenshotFormat;
};

const ScreenshotFormatSettings = memo(() => {
  const { t } = useTranslation();
  // Shown as a compression toggle: WebP is compressed at capture time, PNG is not.
  const screenshotFormatChoices = onOffChoices<RecordingScreenshotFormat>(t, "webp", "png");
  const [initialValues, loadError] = useAsyncInitialValues(
    async (): Promise<ScreenshotFormatFormValues> => ({
      screenshotFormat: (await get_recording_screenshot_format()).screenshot_format,
    }),
    t("settings.screenshot_format_load_failed"),
  );

  return (
    <SettingsRow
      title={t("settings.recording_screenshot_format")}
      description={t("settings.recording_screenshot_format_help")}
    >
      {initialValues ? (
        <SettingsAutoSaveForm
          initialValues={initialValues}
          save={async values => ({
            screenshotFormat: (await put_recording_screenshot_format(values.screenshotFormat))
              .screenshot_format,
          })}
          formFields={["screenshotFormat"]}
          fieldAliases={{ screenshot_format: "screenshotFormat" }}
        >
          {({ setField, values }) => (
            <div className="space-y-1.5">
              <SettingsSegmentedControl
                label={t("settings.recording_screenshot_format")}
                choices={screenshotFormatChoices}
                value={values.screenshotFormat}
                onChange={screenshotFormat => setField("screenshotFormat", screenshotFormat)}
              />
              <SettingsFieldError name="screenshotFormat" />
              <SettingsAutoSaveFeedback />
            </div>
          )}
        </SettingsAutoSaveForm>
      ) : (
        <SettingsLoadState error={loadError} />
      )}
    </SettingsRow>
  );
});
ScreenshotFormatSettings.displayName = "ScreenshotFormatSettings";

/**
 * The user's screenshot blacklist: apps that must never appear in a
 * screenshot. Every add/remove responds with the resulting list, so the
 * rendered state is always what the backend actually stored.
 */
const ScreenshotBlacklistSettings = memo(() => {
  const { t } = useTranslation();
  const [apps, setApps] = useState<string[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  useEffect(() => {
    get_recording_screenshot_blacklist()
      .then(payload => setApps(payload.apps))
      .catch((error: unknown) => {
        setLoadError(
          error instanceof Error ? error.message : t("settings.screenshot_blacklist_load_failed"),
        );
      });
  }, []);

  const change = async (action: () => Promise<{ apps: string[] }>) => {
    setBusy(true);
    setUpdateError(null);
    try {
      setApps((await action()).apps);
    } catch (error: unknown) {
      setUpdateError(
        error instanceof Error ? error.message : t("settings.screenshot_blacklist_update_failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsRow
      title={t("settings.recording_screenshot_blacklist")}
      description={t("settings.recording_screenshot_blacklist_help")}
      alignTop
    >
      {apps ? (
        <div className="space-y-3">
          <form
            onSubmit={event => {
              event.preventDefault();
              const app = draft.trim();
              if (!app) {
                return;
              }
              void change(async () => {
                const payload = await add_recording_screenshot_blacklist_apps([app]);
                setDraft("");
                return payload;
              });
            }}
            className="flex gap-2"
          >
            <input
              value={draft}
              disabled={busy}
              onChange={event => setDraft(event.target.value)}
              aria-label={t("settings.recording_screenshot_blacklist")}
              placeholder={t("settings.screenshot_blacklist_app_placeholder")}
              className="h-10 w-full min-w-0 rounded-lg border border-outline bg-surface px-3 text-sm text-text transition hover:border-text-dim focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60 sm:w-52"
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              className={`${PILL_BUTTON_CLASS} justify-center disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <Plus aria-hidden="true" size={15} />
              {t("settings.screenshot_blacklist_add")}
            </button>
          </form>
          {apps.length === 0 ? (
            <p className="text-xs text-text-mid">{t("settings.screenshot_blacklist_empty")}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {apps.map(app => (
                <li
                  key={app}
                  className="inline-flex items-center gap-1.5 rounded-full border border-outline bg-muted-surface py-1 pl-3 pr-1.5 text-sm text-text"
                >
                  <span className="break-all">{app}</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void change(() => remove_recording_screenshot_blacklist_apps([app]));
                    }}
                    aria-label={t("settings.screenshot_blacklist_remove", { app })}
                    className="rounded-full p-1 text-text-dim transition hover:bg-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X aria-hidden="true" size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {updateError && <p className="text-xs text-danger">{updateError}</p>}
        </div>
      ) : (
        <SettingsLoadState error={loadError} />
      )}
    </SettingsRow>
  );
});
ScreenshotBlacklistSettings.displayName = "ScreenshotBlacklistSettings";

/** The settings-UI label key of a detection category. */
function redactionCategoryLabelKey(category: RedactionCategory) {
  switch (category) {
    case "credentials":
      return "settings.redaction_category_credentials" as const;
    case "accounts":
      return "settings.redaction_category_accounts" as const;
    case "financial":
      return "settings.redaction_category_financial" as const;
    case "personal":
      return "settings.redaction_category_personal" as const;
    case "contact":
      return "settings.redaction_category_contact" as const;
    case "network":
      return "settings.redaction_category_network" as const;
    case "organizations":
      return "settings.redaction_category_organizations" as const;
    case "suspicious":
      return "settings.redaction_category_suspicious" as const;
  }
}

/** The categories with one category's prefs changed. */
function withRedactionCategory(
  categories: RedactionCategorySettings[],
  category: RedactionCategory,
  changes: Partial<Pick<RedactionCategorySettings, "enabled" | "experimental">>,
): RedactionCategorySettings[] {
  return categories.map(entry => (entry.category === category ? { ...entry, ...changes } : entry));
}

/**
 * "?" icon revealing `text` in a tooltip (and as the trigger's accessible
 * label). Needs an ancestor `Tooltip.Provider`.
 */
const HelpTooltip = ({ text }: { text: string }) => (
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <button
        type="button"
        aria-label={text}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-dim transition hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <CircleHelp aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        className="z-[100] max-w-[320px] rounded-md border border-outline bg-surface px-2.5 py-2 text-xs font-normal leading-snug text-text shadow-lg"
      >
        {text}
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
);

/**
 * Whether sensitive text detected in the user's screenshots (emails, card
 * numbers, credentials…) is blacked out before they are saved, which
 * detection categories apply, and a per-category opt-in to the experimental
 * (false-positive-prone) detections. Applies from the next recording start; detection is
 * best-effort.
 */
const ScreenshotRedactionSettings = memo(() => {
  const { t } = useTranslation();
  const { t: tCategoryHelp } = useTranslation("translation", {
    keyPrefix: "settings.redaction_category_help",
  });
  const [initialValues, loadError] = useAsyncInitialValues(
    get_recording_screenshot_redaction,
    t("settings.screenshot_redaction_load_failed"),
  );

  const redactionChoices = onOffChoices(t, true, false);

  if (!initialValues) {
    return (
      <SettingsRow
        title={t("settings.recording_screenshot_redaction")}
        description={t("settings.recording_screenshot_redaction_help")}
      >
        <SettingsLoadState error={loadError} />
      </SettingsRow>
    );
  }

  return (
    <SettingsAutoSaveForm
      initialValues={initialValues}
      save={put_recording_screenshot_redaction}
      formFields={["enabled", "categories"]}
    >
      {({ setField, values }) => (
        <>
          <SettingsRow
            title={t("settings.recording_screenshot_redaction")}
            description={t("settings.recording_screenshot_redaction_help")}
          >
            <div className="space-y-1.5">
              <SettingsSegmentedControl
                label={t("settings.recording_screenshot_redaction")}
                choices={redactionChoices}
                value={values.enabled}
                onChange={enabled => setField("enabled", enabled)}
              />
              <SettingsFieldError name="enabled" />
              <SettingsAutoSaveFeedback />
            </div>
          </SettingsRow>
          {values.enabled && (
            <div className="pb-4">
              <Tooltip.Provider delayDuration={100}>
                <table className="w-full text-sm text-text">
                  <thead>
                    <tr className="text-xs font-medium text-text-mid">
                      <th scope="col" className="py-1 pr-4 text-left font-medium">
                        {t("settings.redaction_categories")}
                      </th>
                      <th scope="col" className="px-2 py-1 text-center font-medium">
                        {t("settings.redaction_category_enabled")}
                      </th>
                      <th scope="col" className="py-1 pl-2 text-center font-medium">
                        <span className="flex items-center justify-center gap-1.5">
                          {t("settings.redaction_category_experimental")}
                          <HelpTooltip text={t("settings.redaction_experimental_help")} />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {values.categories.map(entry => (
                      <tr key={entry.category}>
                        <td className="py-1.5 pr-4">
                          <span className="flex items-center gap-1.5">
                            {t(redactionCategoryLabelKey(entry.category))}
                            <HelpTooltip text={tCategoryHelp(entry.category)} />
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <Switch.Root
                            aria-label={t("settings.redaction_category_enabled")}
                            checked={entry.enabled}
                            onCheckedChange={enabled =>
                              setField(
                                "categories",
                                withRedactionCategory(values.categories, entry.category, {
                                  enabled,
                                }),
                              )
                            }
                            className={`inline-flex h-5 w-9 shrink-0 items-center rounded-full border align-middle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                              entry.enabled
                                ? "border-primary bg-primary"
                                : "border-outline bg-muted-surface"
                            }`}
                          >
                            <Switch.Thumb
                              className={`block h-4 w-4 rounded-full bg-surface shadow-sm ring-1 ring-outline transition-transform duration-200 ease-out motion-reduce:transition-none ${
                                entry.enabled ? "translate-x-4" : "translate-x-0.5"
                              }`}
                            />
                          </Switch.Root>
                        </td>
                        <td className="py-1.5 pl-2 text-center">
                          <label className="group/check relative inline-flex h-5 w-5 cursor-pointer items-center justify-center align-middle before:absolute before:-inset-2 has-disabled:cursor-not-allowed">
                            <input
                              type="checkbox"
                              aria-label={t("settings.redaction_category_experimental")}
                              className="h-4 w-4 cursor-pointer rounded border-outline accent-info transition enabled:group-hover/check:shadow-[0_0_3px_var(--color-info)] disabled:cursor-not-allowed disabled:not-checked:appearance-none disabled:not-checked:border disabled:not-checked:bg-muted-surface"
                              checked={entry.experimental}
                              disabled={!entry.enabled}
                              onChange={event =>
                                setField(
                                  "categories",
                                  withRedactionCategory(values.categories, entry.category, {
                                    experimental: event.target.checked,
                                  }),
                                )
                              }
                            />
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Tooltip.Provider>
              <SettingsFieldError name="categories" />
            </div>
          )}
        </>
      )}
    </SettingsAutoSaveForm>
  );
});
ScreenshotRedactionSettings.displayName = "ScreenshotRedactionSettings";

/**
 * The review-or-upload choice, and the question it raises when switching to
 * automatic upload while sessions already await review: those are only
 * uploaded if the user says so.
 */
const RecordingReviewControl = ({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (reviewBeforeUpload: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const choices = onOffChoices(t, true, false);

  const choose = async (reviewBeforeUpload: boolean) => {
    if (reviewBeforeUpload) {
      onChange(true);
      return;
    }
    // Unlistable sessions just stay in review, which is what not asking
    // amounts to anyway.
    const ids = await get_review_recordings().then(
      payload => payload.recordings.map(recording => recording.id),
      () => [],
    );
    if (ids.length === 0) {
      onChange(false);
    } else {
      setPendingIds(ids);
    }
  };

  const close = () => {
    setPendingIds([]);
    setUploadError(null);
  };

  const keepForReview = () => {
    onChange(false);
    close();
  };

  const uploadPending = async () => {
    setUploading(true);
    setUploadError(null);
    try {
      for (const id of pendingIds) {
        await approve_review_recording(id, []);
        // a retry after a partial failure only approves what is left
        setPendingIds(ids => ids.filter(pendingId => pendingId !== id));
      }
      onChange(false);
      close();
    } catch {
      setUploadError(t("settings.pending_review_upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <SettingsSegmentedControl
        label={t("settings.recording_review_before_upload")}
        choices={choices}
        value={value}
        onChange={reviewBeforeUpload => void choose(reviewBeforeUpload)}
      />
      <Modal
        open={pendingIds.length > 0}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            close();
          }
        }}
        busy={uploading}
      >
        <div className="pr-10">
          <Dialog.Title className="text-lg font-semibold text-text">
            {t("settings.pending_review_title")}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm leading-5 text-text-mid">
            {t("settings.pending_review_message", { count: pendingIds.length })}
          </Dialog.Description>
        </div>
        {uploadError && <p className="mt-4 text-sm text-danger">{uploadError}</p>}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <button
              type="button"
              disabled={uploading}
              className="inline-flex min-h-10 items-center justify-center rounded-lg border border-outline bg-surface px-4 text-sm font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("cancel")}
            </button>
          </Dialog.Close>
          <button
            type="button"
            disabled={uploading}
            onClick={keepForReview}
            className="inline-flex min-h-10 items-center justify-center rounded-lg border border-outline bg-surface px-4 text-sm font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("settings.pending_review_keep")}
          </button>
          <button
            type="button"
            disabled={uploading}
            aria-busy={uploading}
            onClick={() => void uploadPending()}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-on-accent transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploading && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
            {t("settings.pending_review_upload")}
          </button>
        </div>
      </Modal>
    </>
  );
};

/**
 * Whether finished sessions wait for the user's review before they are
 * uploaded, or upload as soon as they are ready. Stored on this computer
 * and synced to the user's account; applies to sessions finishing from now
 * on.
 */
const RecordingReviewSettings = memo(() => {
  const { t } = useTranslation();
  const [initialValues, loadError] = useAsyncInitialValues(
    get_recording_settings,
    t("settings.recording_review_load_failed"),
  );

  return (
    <SettingsRow
      title={t("settings.recording_review_before_upload")}
      description={t("settings.recording_review_before_upload_help")}
    >
      {initialValues ? (
        <SettingsAutoSaveForm
          initialValues={initialValues}
          save={put_recording_settings}
          formFields={["review_before_upload"]}
        >
          {({ setField, values }) => (
            <div className="space-y-1.5">
              <RecordingReviewControl
                value={values.review_before_upload}
                onChange={reviewBeforeUpload =>
                  setField("review_before_upload", reviewBeforeUpload)
                }
              />
              <SettingsFieldError name="review_before_upload" />
              <SettingsAutoSaveFeedback />
            </div>
          )}
        </SettingsAutoSaveForm>
      ) : (
        <SettingsLoadState error={loadError} />
      )}
    </SettingsRow>
  );
});
RecordingReviewSettings.displayName = "RecordingReviewSettings";

// Deliberately untranslated: the same three labels in every language.
const channelChoices: SettingsChoice<NotificationChannel>[] = [
  { id: "in-app", display: "App" },
  { id: "email", display: "Email" },
  { id: "both", display: "App & email" },
];

type PasswordSettingsFormValues = {
  current_password: string;
  new_password: string;
  confirm_password: string;
};

const passwordFields = ["current_password", "new_password", "confirm_password"] as const;
// Field names double as form value keys, so the two settings-section labels
// need an explicit key map rather than renamed fields.
const passwordFieldLabelKeys = {
  current_password: "settings.current_password",
  new_password: "settings.new_password",
  confirm_password: "confirm_password",
} as const;

const passwordInitialValues: PasswordSettingsFormValues = {
  current_password: "",
  new_password: "",
  confirm_password: "",
};

const ChangePasswordSettings = memo(() => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <>
      <SettingsSection title={t("settings.section_security")}>
        <div className="divide-y divide-outline">
          <SettingsRow
            title={t("settings.change_password")}
            description={t("settings.change_password_help")}
          >
            <div className="flex flex-wrap items-center gap-3 sm:justify-end">
              <span aria-live="polite" className="text-xs text-success">
                {saved && t("settings.change_password_saved")}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSaved(false);
                  setOpen(true);
                }}
                className={`${PILL_BUTTON_CLASS} justify-center`}
              >
                <KeyRound aria-hidden="true" size={15} />
                {t("settings.change_password")}
              </button>
            </div>
          </SettingsRow>
          <MfaSettings />
        </div>
      </SettingsSection>

      {/* Formik wraps the modal so its `isSubmitting` can lock the dialog; the
          fields are reset whenever the dialog closes. */}
      <Formik<PasswordSettingsFormValues>
        initialValues={passwordInitialValues}
        validate={values =>
          values.new_password !== values.confirm_password
            ? { confirm_password: t("passwords_do_not_match") }
            : {}
        }
        onSubmit={async (values, actions) => {
          actions.setStatus(null);
          setSaved(false);
          actions.setErrors({});
          try {
            await change_own_password({
              current_password: values.current_password,
              new_password: values.new_password,
            });
            actions.resetForm();
            setSaved(true);
            setOpen(false);
          } catch (error: unknown) {
            const parsed = splitApiRequestFormErrors<PasswordSettingsFormValues>(
              error,
              t,
              passwordFields,
              { password: "new_password" },
            );
            actions.setTouched(parsed.touched, false);
            actions.setErrors(parsed.fieldErrors);
            actions.setStatus(parsed.remainder);
          }
        }}
      >
        {({ isSubmitting, isValid, resetForm, status, values }) => (
          <Modal
            open={open}
            onOpenChange={nextOpen => {
              if (!nextOpen) {
                resetForm();
              }
              setOpen(nextOpen);
            }}
            busy={isSubmitting}
          >
            <div className="pr-10">
              <Dialog.Title className="text-lg font-semibold text-text">
                {t("settings.change_password")}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm leading-5 text-text-mid">
                {t("settings.change_password_help")}
              </Dialog.Description>
            </div>
            <Form className="mt-5 space-y-4">
              {passwordFields.map(name => (
                <label key={name} className="block space-y-1">
                  <span className="text-sm font-medium text-text">
                    {t(passwordFieldLabelKeys[name])}
                  </span>
                  <TextInput name={name} type="password" wrapped={false} disabled={isSubmitting} />
                </label>
              ))}
              <ErrorBlockGeneric error={status} />
              <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className="inline-flex min-h-10 items-center justify-center rounded-lg border border-outline bg-surface px-4 text-sm font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("cancel")}
                  </button>
                </Dialog.Close>
                <button
                  type="submit"
                  disabled={!isValid || isSubmitting || passwordFields.some(name => !values[name])}
                  aria-busy={isSubmitting}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-on-accent transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  ) : (
                    <KeyRound aria-hidden="true" className="h-4 w-4" />
                  )}
                  {t("settings.change_password")}
                </button>
              </div>
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
});
ChangePasswordSettings.displayName = "ChangePasswordSettings";

const MfaSettings = memo(() => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [enroll, setEnroll] = useState<MfaEnrollStartResponse | null>(null);
  const [enrollCode, setEnrollCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get_mfa_status()
      .then(status => setEnabled(status.enabled))
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error.message : t("settings.mfa_load_failed"));
      });
  }, []);

  const startEnrollment = async () => {
    setBusy(true);
    setError(null);
    try {
      setEnroll(await start_mfa_enrollment());
      setEnrollCode("");
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : t("settings.mfa_start_failed"));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrollment = async () => {
    if (!enroll) return;
    setBusy(true);
    setError(null);
    try {
      const status = await confirm_mfa_enrollment({
        secret: enroll.secret,
        code: enrollCode,
      });
      setEnabled(status.enabled);
      setEnroll(null);
      setEnrollCode("");
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : t("settings.mfa_confirm_failed"));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const status = await disable_mfa({ code: disableCode });
      setEnabled(status.enabled);
      setDisableOpen(false);
      setDisableCode("");
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : t("settings.mfa_disable_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SettingsRow
        title={t("settings.mfa")}
        description={t(enabled ? "settings.mfa_enabled_help" : "settings.mfa_disabled_help")}
      >
        {enabled === null ? (
          <SettingsLoadState error={loadError} />
        ) : (
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <span className={`text-xs ${enabled ? "text-success" : "text-text-mid"}`}>
              {enabled ? t("settings.mfa_enabled") : t("settings.mfa_disabled")}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                if (enabled) {
                  setDisableOpen(true);
                } else {
                  void startEnrollment();
                }
              }}
              className={`${PILL_BUTTON_CLASS} justify-center disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {enabled ? (
                <ShieldOff aria-hidden="true" size={15} />
              ) : (
                <Shield aria-hidden="true" size={15} />
              )}
              {enabled ? t("settings.mfa_disable") : t("settings.mfa_enable")}
            </button>
          </div>
        )}
      </SettingsRow>

      <Modal open={enroll !== null} onOpenChange={open => !open && setEnroll(null)} busy={busy}>
        <div className="pr-10">
          <Dialog.Title className="text-lg font-semibold text-text">
            {t("settings.mfa_enable")}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm leading-5 text-text-mid">
            {t("settings.mfa_enroll_help")}
          </Dialog.Description>
        </div>
        {enroll && (
          <div className="mt-5 space-y-4">
            <div className="flex flex-col items-center gap-3 rounded-lg border border-outline bg-white p-4">
              <QRCodeSVG
                value={enroll.otpauth_uri}
                size={192}
                level="M"
                marginSize={2}
                title={t("settings.mfa_qr_code")}
              />
              <span className="text-center text-xs font-medium text-slate-700">
                {t("settings.mfa_qr_code")}
              </span>
            </div>
            <div className="rounded-lg border border-outline bg-muted-surface p-3">
              <div className="flex items-center justify-between gap-3">
                <code className="break-all text-sm text-text">{enroll.secret}</code>
                <button
                  type="button"
                  onClick={() => void navigator.clipboard?.writeText(enroll.secret)}
                  className="rounded-lg p-2 text-text-dim transition hover:bg-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={t("settings.mfa_copy_secret")}
                >
                  <Copy aria-hidden="true" size={15} />
                </button>
              </div>
            </div>
            <a
              href={enroll.otpauth_uri}
              className="inline-flex text-sm font-medium text-primary hover:text-primary/80"
            >
              {t("settings.mfa_open_authenticator")}
            </a>
            <label className="block space-y-1">
              <span className="text-sm font-medium text-text">{t("settings.mfa_code")}</span>
              <input
                value={enrollCode}
                onChange={event => setEnrollCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                className="block w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm text-text focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              />
            </label>
            <ErrorBlockGeneric error={error} />
            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={busy}
                  className="inline-flex min-h-10 items-center justify-center rounded-lg border border-outline bg-surface px-4 text-sm font-medium text-text transition hover:bg-muted-surface disabled:opacity-50"
                >
                  {t("cancel")}
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={busy || enrollCode.trim().length < 6}
                onClick={() => void confirmEnrollment()}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-on-accent transition hover:bg-primary/90 disabled:opacity-50"
              >
                {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : null}
                {t("settings.mfa_confirm")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={disableOpen} onOpenChange={setDisableOpen} busy={busy}>
        <div className="pr-10">
          <Dialog.Title className="text-lg font-semibold text-text">
            {t("settings.mfa_disable")}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm leading-5 text-text-mid">
            {t("settings.mfa_disable_help")}
          </Dialog.Description>
        </div>
        <div className="mt-5 space-y-4">
          <label className="block space-y-1">
            <span className="text-sm font-medium text-text">{t("settings.mfa_code")}</span>
            <input
              value={disableCode}
              onChange={event => setDisableCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="block w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm text-text focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            />
          </label>
          <ErrorBlockGeneric error={error} />
          <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                className="inline-flex min-h-10 items-center justify-center rounded-lg border border-outline bg-surface px-4 text-sm font-medium text-text transition hover:bg-muted-surface disabled:opacity-50"
              >
                {t("cancel")}
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={busy || disableCode.trim().length < 6}
              onClick={() => void disable()}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-danger px-4 text-sm font-medium text-white transition hover:bg-danger/90 disabled:opacity-50"
            >
              {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : null}
              {t("settings.mfa_disable")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
});
MfaSettings.displayName = "MfaSettings";

/**
 * Which channel alert results arrive over and how often the email digests
 * may go out — the frequency only shows when the channel has an email leg.
 * A user who never saved gets the backend's defaults back.
 */
const NotificationSettings = memo(() => {
  const { t } = useTranslation();
  const [initialValues, loadError] = useAsyncInitialValues(
    get_notification_settings,
    t("settings.notification_settings_load_failed"),
  );

  const frequencyChoices: SettingsChoice<NotificationFrequency>[] = [
    { id: "immediately", display: t("settings.notification_frequency_immediately") },
    { id: "daily", display: t("settings.notification_frequency_daily") },
    { id: "weekly", display: t("settings.notification_frequency_weekly") },
    { id: "monthly", display: t("settings.notification_frequency_monthly") },
  ];

  // Where in-app notifications cannot be shown, the help text and warning say
  // so and "in-app" is not offered anew (the stored value stays visible until
  // the user moves away from it).
  const { inAppNotifications } = frontendCapabilities;
  const channelDescription = t(
    inAppNotifications
      ? "settings.notification_channel_help"
      : "settings.notification_channel_help_saas",
  );
  const inAppWarning = t(
    inAppNotifications
      ? "settings.notification_channel_in_app_warning"
      : "settings.notification_channel_in_app_saas_warning",
  );

  return (
    <SettingsSection
      title={t("settings.section_notifications")}
      controlSegments={channelChoices.length}
    >
      {initialValues ? (
        <SettingsAutoSaveForm
          initialValues={initialValues}
          save={put_notification_settings}
          formFields={["frequency", "channel"]}
        >
          {({ setField, values }) => {
            const inApp = values.channel === "in-app";
            return (
              <>
                <div className="divide-y divide-outline">
                  <SettingsRow
                    title={t("settings.notification_channel")}
                    description={channelDescription}
                    alignTop={inApp}
                  >
                    <SettingsControlSlot className="space-y-3">
                      <SettingsSegmentedControl
                        label={t("settings.notification_channel")}
                        choices={
                          !inAppNotifications && !inApp
                            ? channelChoices.filter(choice => choice.id !== "in-app")
                            : channelChoices
                        }
                        value={values.channel}
                        onChange={channel => setField("channel", channel)}
                      />
                      <SettingsFieldError name="channel" />
                      {/* The frequency is still sent while its row is hidden, so
                          an error on it needs a home here. */}
                      {inApp && <SettingsFieldError name="frequency" />}
                      {inApp && (
                        <div className="flex gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-xs leading-5 text-warning-strong">
                          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                          <span>{inAppWarning}</span>
                        </div>
                      )}
                    </SettingsControlSlot>
                  </SettingsRow>
                  {!inApp && (
                    <SettingsRow
                      title={t("settings.notification_frequency")}
                      description={t("settings.notification_frequency_help")}
                    >
                      <SettingsSelect
                        label={t("settings.notification_frequency")}
                        choices={frequencyChoices}
                        value={values.frequency}
                        onChange={frequency => setField("frequency", frequency)}
                      />
                      <SettingsFieldError name="frequency" />
                    </SettingsRow>
                  )}
                </div>
                {/* One feedback slot for the whole form, tucked into the bottom
                    padding of whichever row closes the card. */}
                <div className="-mt-4 pb-4">
                  <SettingsControlSlot>
                    <SettingsAutoSaveFeedback />
                  </SettingsControlSlot>
                </div>
              </>
            );
          }}
        </SettingsAutoSaveForm>
      ) : (
        <SettingsRow title={t("settings.notification_channel")} description={channelDescription}>
          <SettingsLoadState error={loadError} />
        </SettingsRow>
      )}
    </SettingsSection>
  );
});
NotificationSettings.displayName = "NotificationSettings";

const Settings = () => {
  const { autoAnalyze, language, setAutoAnalyze, setLanguage, setTheme, theme } = useSettings();
  const {
    openRecordingPermissions,
    recordingPermissionsAvailable,
    recordingPermissionsRequired,
    recordingPermissionsRecommended,
    recordingStatus,
    handleStopRecording,
  } = useRecordingControls();
  const recordingPermissionsMissing =
    recordingPermissionsRequired || recordingPermissionsRecommended;
  const [capturePrivacyOpen, setCapturePrivacyOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  usePageTitle(t("Settings"));

  const autoAnalyzeChoices = onOffChoices(t, true, false);
  const themeChoices: SettingsChoice<"light" | "dark">[] = [
    { id: "light", display: t("settings.theme_light") },
    { id: "dark", display: t("settings.theme_dark") },
  ];

  useEffect(() => {
    if (!frontendCapabilities.localUpdates) {
      return;
    }

    setUpdateBusy(true);
    check_for_updates()
      .then(setUpdateStatus)
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : t("unable_to_check_updates");
        setUpdateError(message);
      })
      .finally(() => {
        setUpdateBusy(false);
      });
  }, []);

  const handleUpdateAction = async (action: () => Promise<UpdateStatus | { status: string }>) => {
    setUpdateBusy(true);
    setUpdateError(null);
    try {
      const result = await action();
      if (isUpdateStatus(result)) {
        setUpdateStatus(result);
      }
    } catch (error: unknown) {
      setUpdateError(error instanceof Error ? error.message : t("update_failed"));
    } finally {
      setUpdateBusy(false);
    }
  };
  const recordingMustStopBeforeUpdate =
    recordingStatus === "recording" || recordingStatus === "paused";

  const handleInstallUpdate = async () => {
    await handleUpdateAction(async () => {
      if (recordingMustStopBeforeUpdate) {
        await handleStopRecording();
      }
      return apply_update();
    });
  };

  return (
    <Container>
      <div className="px-4 pb-16 pt-7 sm:px-6">
        <div className="mx-auto max-w-3xl">
          {frontendCapabilities.localRecording && (
            <SettingsSection title={t("recording")}>
              <div className="divide-y divide-outline">
                <SettingsRow
                  title={t("settings.auto_analyze_recordings")}
                  description={t("settings.auto_analyze_recordings_help")}
                >
                  <SettingsSegmentedControl
                    label={t("settings.auto_analyze_recordings")}
                    choices={autoAnalyzeChoices}
                    value={autoAnalyze}
                    onChange={setAutoAnalyze}
                  />
                </SettingsRow>
                <ScreenshotFormatSettings />
                {recordingPermissionsAvailable && (
                  <SettingsRow
                    title={t("recording_permissions")}
                    description={t(
                      recordingPermissionsRequired
                        ? "recording_permissions_modal_description"
                        : recordingPermissionsRecommended
                          ? "recording_permissions_recommended_description"
                          : "recording_permissions_ready_description",
                    )}
                  >
                    <div className="flex sm:justify-end">
                      <button
                        type="button"
                        onClick={openRecordingPermissions}
                        className={PILL_BUTTON_CLASS}
                      >
                        {recordingPermissionsMissing ? (
                          <AlertTriangle
                            aria-hidden="true"
                            className="text-warning-strong"
                            size={15}
                          />
                        ) : (
                          <ShieldCheck aria-hidden="true" className="text-success" size={15} />
                        )}
                        {t(
                          recordingPermissionsMissing
                            ? "set_recording_permissions"
                            : "review_recording_permissions",
                        )}
                        <ChevronRight aria-hidden="true" className="text-text-dim" size={15} />
                      </button>
                    </div>
                  </SettingsRow>
                )}
              </div>
            </SettingsSection>
          )}

          {frontendCapabilities.localRecording && (
            <SettingsSection
              title={t("settings.section_privacy")}
              description={
                <button
                  type="button"
                  onClick={() => setCapturePrivacyOpen(true)}
                  className="flex w-fit items-center gap-1.5 rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Info aria-hidden="true" size={16} className="shrink-0" />
                  {t("what_platonic_records")}
                </button>
              }
            >
              <div className="divide-y divide-outline">
                <ScreenshotBlacklistSettings />
                <ScreenshotRedactionSettings />
                <RecordingReviewSettings />
              </div>
            </SettingsSection>
          )}

          <SettingsSection
            title={t("settings.section_appearance")}
            controlSegments={themeChoices.length}
          >
            <div className="divide-y divide-outline">
              <SettingsRow title={t("settings.theme")}>
                <SettingsSegmentedControl
                  label={t("settings.theme")}
                  choices={themeChoices}
                  value={theme}
                  onChange={setTheme}
                />
              </SettingsRow>
              <SettingsRow title={t("language")}>
                <SettingsSelect
                  label={t("language")}
                  choices={supportedLanguages}
                  value={language}
                  onChange={value => {
                    setLanguage(value);
                    void i18n.changeLanguage(value);
                  }}
                />
              </SettingsRow>
            </div>
          </SettingsSection>

          <NotificationSettings />
          <ChangePasswordSettings />
        </div>
      </div>
      {frontendCapabilities.localRecording && (
        <CapturePrivacyModal
          open={capturePrivacyOpen}
          onClose={() => setCapturePrivacyOpen(false)}
        />
      )}
      {frontendCapabilities.localUpdates && (
        <UpdateInstallModal
          status={updateStatus}
          busy={updateBusy || recordingStatus === "stopping"}
          error={updateError}
          requiresRecordingStop={recordingMustStopBeforeUpdate}
          onApply={() => {
            void handleInstallUpdate();
          }}
        />
      )}
    </Container>
  );
};

export default Settings;
